import { useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { classnames } from 'helpers/utils';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { AlbumUploadEntryProps } from 'context/self-recording-context/context-reducer';
import styles from './uploading-card.module.scss';

interface UploadingCardProps {
  upload: AlbumUploadEntryProps;
  // Refetches the album. Awaited before this placeholder is removed, so the
  // real card is already in the grid when the swap happens — same slot, same
  // render, no gap.
  onSuccess: () => void | Promise<void>;
}

// How long the finish morph (ring completes, shimmer fades) stays visible
// before the swap. Keep in sync with $finish-ms in the scss.
const FINISH_MS = 500;
const ERROR_REMOVE_MS = 3000;

// Placeholder memory card for an in-flight entry. The name and date are known
// client-side, so they render immediately and never change; only the thumbnail
// area animates (shimmer + orbit ring) until the real content takes its place.
export const UploadingCard = ({ upload, onSuccess }: UploadingCardProps) => {
  const { dispatch } = useContext(SelfRecordingContext);
  const [finishing, setFinishing] = useState(false);
  const finishStartedRef = useRef(false);
  // A new entry is created "now" — its date is today, stable from the start.
  const dateLabel = useRef(dayjs().format('MMMM D, YYYY')).current;

  const removeSelf = () =>
    dispatch({
      type: selfRecordingActions.removeUpload,
      removeUpload: upload.uploadId,
    });

  useEffect(() => {
    if (upload.error) {
      const timer = setTimeout(removeSelf, ERROR_REMOVE_MS);
      return () => clearTimeout(timer);
    }
    if (!upload.isUploading && !finishStartedRef.current) {
      finishStartedRef.current = true;
      setFinishing(true);
      (async () => {
        // Refetch FIRST, and keep the finish animation on screen meanwhile:
        // removing the placeholder then lands in the same render as the real
        // card appearing, so the card visually "just loads" in place.
        await Promise.all([
          Promise.resolve(onSuccess()),
          new Promise((resolve) => {
            setTimeout(resolve, FINISH_MS);
          }),
        ]);
        removeSelf();
      })();
    }
    return undefined;
  }, [upload.isUploading, upload.error]);

  return (
    <div
      className={classnames(styles.card, {
        [styles.finishing]: finishing,
        [styles.failed]: !!upload.error,
      })}
    >
      <span className={styles.thumb}>
        <span className={styles.shimmer} />
        <span className={styles.loader}>
          <span className={styles.ring} />
          <span className={styles.corePulse} />
        </span>
      </span>
      <span className={styles.titleBlock}>
        <span className={styles.title}>{upload.name || 'New memory'}</span>
        <span className={styles.subtitle}>
          {upload.error ? 'Upload failed' : dateLabel}
        </span>
      </span>
    </div>
  );
};
